import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Observable, forkJoin, map } from 'rxjs';
import { MessageService } from 'primeng/api';
import { AutoComplete, AutoCompleteCompleteEvent, AutoCompleteSelectEvent } from 'primeng/autocomplete';
import { SelectChangeEvent } from 'primeng/select';
import _ from 'lodash';
import { format } from 'date-fns';
import { TranslateService } from '@ngx-translate/core';
import { Business, BusinessEntityType, BusinessSearch } from '../../../models/finance/business';
import { Fabric } from '../../../models/fabric/fabric';
import { GroupedEntries, RequiredItem, RequisitionEntriesFormGroup } from '../../../models/yarn-requisition/yarn-requisition-form';
import { InventoryTransaction, InventoryTransactionType } from '../../../models/inventory/inventory-transaction';
import { ItemMaster } from '../../../models/item/item-master';
import { KnittingOrder, KnittingOrderSearchCommand, KnittingOrderStatus } from '../../../models/production/knitting-order';
import { MatchingItemMasterCommand } from '../../../models/item/yarn/yarn-search';
import { Provider } from '../../../models/inventory/lot-item';
import { Requisition, RequisitionEntry, RequisitionStatus } from '../../../models/requisitions/requisition';
import { RequisitionReport } from '../../../models/reports/requisition-report';
import { TransferUnit } from '../../../models/inventory/transfer-unit';
import { Translation } from '../../../models/translation';
import { Warehouse } from '../../../models/warehouse.ts/warehouse';
import { WorkStation } from '../../../models/item/work-station';
import { InventoryTransactionNeededElement } from '../../../models/inventory/inventory-transaction-needed-element';
import { InventoryTransactionService } from '../../../services/base-actions/inventory-transaction.service';
import { InventoryTransactionManagerFactory } from '../../../services/factory/inventory-transaction.factory';
import {
  EntityKeyByReference,
  EnumValues,
  GlobalEntityDefinition,
  ModalService,
  Note,
  NotesSidebarComponent,
  UserLite,
  EnumObjectExtended,
  BreadcrumbService,
} from '@card-software/library';
import { AuthService, SiteService } from '@card-software/nexus-utilities';
import { EnumService } from '../../../services/enum.service';
import { BusinessProxyService } from '../../../proxies/finance/business.proxy.service';
import { FabricProxyService } from '../../../proxies/item/fabric.proxy.service';
import { KnittingOrderProxyService } from '../../../proxies/production/knitting-order.proxy.service';
import { LotItemProxyService } from '../../../proxies/inventory/lot-item.proxy.service';
import { RequisitionProxyService } from '../../../proxies/inventory/requisition.proxy.service';
import { ReportProxyService } from '../../../proxies/report.proxy.service';
import { WarehouseProxyService } from '../../../proxies/inventory/warehouse.proxy';
import { WorkStationProxyService } from '../../../proxies/item/work-station.proxy';
import { YarnProxyService } from '../../../proxies/item/yarn.proxy.service';
import { UserAuthenticationService } from '../../../services/user-authentication.service';
import { LocalHelperService } from '../../../services/local-helper.service';
import { ImportantLocations } from '../../../models/location';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { UserGroup } from '../../../models/authorization/enum/user-group.enum';

@Component({
  selector: 'app-yarn-requisitions-details',
  templateUrl: './yarn-requisitions-details.component.html',
  styleUrl: './yarn-requisitions-details.component.scss',
})
export class YarnRequisitionsDetailsComponent implements OnInit, OnDestroy {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly fb = inject(FormBuilder);
  private readonly inventoryTransactionManagerFactory = inject(InventoryTransactionManagerFactory);
  private readonly enumService = inject(EnumService);
  private readonly reportProxyService = inject(ReportProxyService);
  private readonly workStationProxyService = inject(WorkStationProxyService);
  private readonly warehouseProxyService = inject(WarehouseProxyService);
  private readonly businessProxyService = inject(BusinessProxyService);
  private readonly fabricProxyService = inject(FabricProxyService);
  private readonly knittingOrderProxyService = inject(KnittingOrderProxyService);
  private readonly lotItemProxyService = inject(LotItemProxyService);
  private readonly messageService = inject(MessageService);
  private readonly modalService = inject(ModalService);
  private readonly requisitionProxyService = inject(RequisitionProxyService);
  private readonly translateService = inject(TranslateService);
  private readonly userAuthenticationService = inject(UserAuthenticationService);
  private readonly yarnProxyService = inject(YarnProxyService);
  public readonly localHelperService = inject(LocalHelperService);
  private readonly breadcrumbService = inject(BreadcrumbService);

  inventoryTransactionManager = this.inventoryTransactionManagerFactory.createManager('requisition-details');
  groupedEntries: GroupedEntries[] = [];
  allFabrics: Fabric[] = [];
  translations: Record<string, Translation> = {
    Knitting: {
      es: 'Tejido',
      en: 'Knitting',
    },
    Warehouse: {
      es: 'Almacén',
      en: 'Warehouse',
    },
    Form_Has_Invalid_Fields: {
      es: 'El formulario tiene campos inválidos. Por favor, complete los campos requeridos.',
      en: 'Form has invalid fields. Please fill the required fields.',
    },
    Requisitions_Created_Successfully: {
      es: "Requisición '{{name}}' creada con éxito.",
      en: "Requisition '{{name}}' created successfully.",
    },
    Requisition_Updated_Successfully: {
      es: "Requisición '{{number}}' actualizada con éxito.",
      en: "Requisition '{{number}}' updated successfully.",
    },
    Success: {
      es: 'Exitoso',
      en: 'Success',
    },
    Requisition_Status_Updated: {
      es: 'Estado de la requisición {{number}} actualizado a {{newStatus}}.',
      en: 'Requisition {{number}} status updated to {{newStatus}}.',
    },
    Requisition_Status_Error_Updating: {
      es: 'Error al actualizar el estado de la requisición: \n {{error}}',
      en: 'Error updating requisition status: \n {{error}}',
    },
    Note_Added_Successfully: {
      es: 'Nota añadida con éxito.',
      en: 'Note added successfully.',
    },
  };
  isLoading = 0;
  fabricSuggestions: string[] = [];
  requisition: Requisition | null = null;
  originalRequisition?: Requisition;
  providersOptions: Business[] = [];
  itemMasterOptions: ItemMaster[] = [];
  selectedFabrics: {
    fabricCode: string;
    totalQuantity: number;
    yarnDetails: {
      yarnCode: string;
      usePercentage: number;
      quantity: number;
    }[];
  }[] = [];
  statusEnumObject: EnumObjectExtended = this.enumService.getEnum('RequisitionStatus', 'inventory');
  statuses: EnumValues = this.statusEnumObject.values;
  statusesIcon: EnumValues = this.statusEnumObject.valuesEnglish;
  unitOfMeasures: EnumValues = this.enumService.getEnum('UnitOfMeasure', 'item').values;
  transactionType: EnumValues = this.enumService.getEnum('TransactionType', 'inventory').values;
  warehouses: Warehouse[] = [];
  workStations: WorkStation[] = [];
  providers: Provider[] = [];
  mappedFabrics: Fabric[] = [];
  user: UserLite | null = null;
  inventoryTransaction: InventoryTransaction | null = null;
  inventoryTransactionNeededElement: InventoryTransactionNeededElement[] = [];
  loading = 0;
  originalTransferUnit?: TransferUnit;
  yarnAndItemMaster: Record<string, ItemMaster | null> = {};

  // toggle data options
  dataView: FormControl<'knitting' | 'warehouse' | null> = new FormControl('knitting');
  dataOptions: { label: string; value: 'knitting' | 'warehouse'; disabled: boolean }[] = [];
  initDataOptions() {
    this.dataOptions = [
      {
        label: this.translations['Knitting'][this.translateService.currentLang as keyof Translation],
        value: 'knitting',
        disabled: this.availableTab() === 'warehouse',
      },
      {
        label: this.translations['Warehouse'][this.translateService.currentLang as keyof Translation],
        value: 'warehouse',
        disabled: this.availableTab() === 'knitting',
      },
    ];
  }

  get showRequisitionInProgressError(): boolean {
    const valid = [RequisitionStatus.InRoute, RequisitionStatus.Received, RequisitionStatus.Rejected];
    return this.status.value !== RequisitionStatus.Processing && !valid.includes(this.status.value!);
  }

  get showInventoryTransactionsOnly(): boolean {
    const valid = [RequisitionStatus.InRoute, RequisitionStatus.Received, RequisitionStatus.Rejected];
    return valid.includes(this.status.value!);
  }

  get canEditInventoryTransactions(): boolean {
    if (this.showInventoryTransactionsOnly) {
      return false;
    }
    return this.canEdit();
  }

  get requisitionInRoute(): boolean {
    return this.status.value === RequisitionStatus.InRoute;
  }

  get readonlyKnittingSection(): boolean {
    return this.status.value !== RequisitionStatus.Requested;
  }

  get showSaveButton(): boolean {
    return this.status.value === RequisitionStatus.Requested || _.isNil(this.originalRequisition);
  }

  get canChangeLocation(): boolean {
    // Can only change location if inventory transaction hasn't been saved yet (no ID)
    // OR if status is still in Requested state (before Processing)
    const hasUnsavedTransaction =
      _.isNil(this.inventoryTransactions) ||
      this.inventoryTransactions.length === 0 ||
      this.inventoryTransactions.every((t) => _.isNil(t.id));

    return hasUnsavedTransaction;
  }

  get isProcessingStatus(): boolean {
    return this.status.value === RequisitionStatus.Processing;
  }

  get locationChangeLockedMessage(): string | null {
    if (this.canChangeLocation) {
      return null;
    }

    return 'Location cannot be changed after inventory transaction has been saved. If you need to change the source location, you must void this requisition and create a new one.';
  }

  get currentFromLocationCode(): string | undefined {
    return this.inventoryTransaction?.fromLocationCode;
  }

  get validStatuses(): EnumValues {
    const currentStatus = this.status.value;
    const allStatus = this.statuses ?? {};
    switch (currentStatus) {
      case RequisitionStatus.Requested:
        // Return a new object excluding Requested
        return Object.keys(allStatus)
          .filter((key) => ![RequisitionStatus.Rejected, RequisitionStatus.InRoute, RequisitionStatus.Received].includes(Number(key)))
          .reduce((acc, key) => {
            acc[Number(key)] = allStatus[Number(key)];
            return acc;
          }, {} as EnumValues);
      case RequisitionStatus.Processing:
        // Return a new object excluding InRoute
        return Object.keys(allStatus)
          .filter((key) => ![RequisitionStatus.Requested, RequisitionStatus.Rejected, RequisitionStatus.Received].includes(Number(key)))
          .reduce((acc, key) => {
            acc[Number(key)] = allStatus[Number(key)];
            return acc;
          }, {} as EnumValues);
      case RequisitionStatus.InRoute:
        // Return a new object excluding InRoute and Processing
        return Object.keys(allStatus)
          .filter((key) => ![RequisitionStatus.Requested, RequisitionStatus.Processing].includes(Number(key)))
          .reduce((acc, key) => {
            acc[Number(key)] = allStatus[Number(key)];
            return acc;
          }, {} as EnumValues);
      case RequisitionStatus.Rejected:
        // Return a new object excluding Rejected
        return Object.keys(allStatus)
          .filter((key) => ![RequisitionStatus.Requested, RequisitionStatus.InRoute, RequisitionStatus.Received].includes(Number(key)))
          .reduce((acc, key) => {
            acc[Number(key)] = allStatus[Number(key)];
            return acc;
          }, {} as EnumValues);
      case RequisitionStatus.Received:
        return [] as EnumValues; // No valid statuses after receiving

      case RequisitionStatus.Voided:
        return [] as EnumValues; // No valid statuses after voiding
    }
    return allStatus;
  }

  public get roleStatusOptions(): EnumValues {
    if (!this.validStatuses || typeof this.validStatuses !== 'object') return {};

    if (
      AuthService.hasRole(UserGroup.InternalYarnRequisitionAdmin) ||
      (AuthService.hasRole(UserGroup.InternalYarnRequisitionKnittingEditor) &&
        AuthService.hasRole(UserGroup.InternalYarnRequisitionWarehouseEditor))
    ) {
      return this.validStatuses;
    }

    if (AuthService.hasRole(UserGroup.InternalYarnRequisitionKnittingEditor)) {
      const allowedValues = [0, 3, 4, 5];
      const filtered: EnumValues = {};

      for (const key of allowedValues) {
        if (this.validStatuses[key]) {
          filtered[key] = this.validStatuses[key];
        }
      }

      return filtered;
    }

    if (AuthService.hasRole(UserGroup.InternalYarnRequisitionWarehouseEditor)) {
      const allowedValues = [1, 2];
      const filtered: EnumValues = {};

      for (const key of allowedValues) {
        if (this.validStatuses[key]) {
          filtered[key] = this.validStatuses[key];
        }
      }

      return filtered;
    }

    if (this.canEdit()) {
      return this.validStatuses;
    }

    return {};
  }
  //Knitting Orders
  knittingOrders: KnittingOrder[] = [];

  // Forms and controls
  status: FormControl<number | null> = new FormControl(0, [Validators.required]);
  knittingOrder: FormControl<KnittingOrder> = new FormControl();
  requisitionEntries: FormArray<FormGroup<RequisitionEntriesFormGroup>> = new FormArray<FormGroup<RequisitionEntriesFormGroup>>(
    [],
    [Validators.required],
  );
  requisitionsDetailsForm!: FormGroup;
  requisitionsDetailsFormSubmitted = false;

  // properties for notes component
  userLite: UserLite | null = this.userAuthenticationService.userLite;
  notes: Note[] = [];
  // QR Scanner properties
  uniqueScanned: string[] = [];

  // Location source options
  locationSource: FormControl<string | null> = new FormControl('NORMAL'); // 'NORMAL', 'SIMTEX', or 'INITIAL'
  locationSourceOptions = [
    { label: 'Normal Warehouse (NT.WH-RM-1)', value: 'NORMAL' },
    { label: 'SIMTEX Warehouse (SIMTEX.WH-RM-2)', value: 'SIMTEX' },
    { label: 'Initial Virtual Warehouse (NT.INITIAL-WH-RM-1)', value: 'INITIAL' },
  ];

  constructor() {
    this.initDataOptions();
    this.setRequisitionsDetailsForm();
  }

  canEdit() {
    // if Admin or Editor
    if (
      AuthService.hasRole(UserGroup.InternalYarnRequisitionKnittingEditor) ||
      AuthService.hasRole(UserGroup.InternalYarnRequisitionWarehouseEditor) ||
      AuthService.hasRole(UserGroup.InternalYarnRequisitionAdmin)
    ) {
      return true;
    }
    // if Viewer
    if (
      AuthService.hasRole(UserGroup.InternalYarnRequisitionAllViewer) ||
      AuthService.hasRole(UserGroup.InternalYarnRequisitionKnittingViewer) ||
      AuthService.hasRole(UserGroup.InternalYarnRequisitionWarehouseViewer)
    ) {
      return false;
    }
    // Default
    return false;
  }

  availableTab(): string {
    if (AuthService.hasRole(UserGroup.InternalYarnRequisitionAdmin)) {
      this.dataView.setValue('knitting');
      return 'both';
    }

    if (
      AuthService.hasRole(UserGroup.InternalYarnRequisitionKnittingViewer) ||
      AuthService.hasRole(UserGroup.InternalYarnRequisitionKnittingEditor)
    ) {
      this.dataView.setValue('knitting');
      return 'knitting';
    }

    if (
      AuthService.hasRole(UserGroup.InternalYarnRequisitionWarehouseEditor) ||
      AuthService.hasRole(UserGroup.InternalYarnRequisitionWarehouseViewer)
    ) {
      this.dataView.setValue('warehouse');
      return 'warehouse';
    }

    this.dataView.setValue('knitting');
    return 'both';
  }

  ngOnDestroy(): void {
    this.inventoryTransactionManagerFactory.deleteManager('requisition-details');
  }

  ngOnInit(): void {
    this.initDataOptions();
    forkJoin([this.workStationProxyService.getAll(), this.warehouseProxyService.getAllWarehouses()]).subscribe({
      next: ([workStationsResponse, warehousesResponse]) => {
        let workStations = workStationsResponse.data;
        const warehouses = warehousesResponse.data?.map((w) => ({
          ...w,
          fieldToDisplay: '(' + w.code + ') ' + w.name,
        }));

        workStations = workStations!.map((w) => ({
          ...w,
          fieldToDisplay: w.name,
        }));

        this.warehouses = warehouses!;
        this.workStations = workStations;
      },
    });
    this.status.valueChanges.subscribe((value) => {
      if (value !== null) {
        this.updateStatus(value);
      }
    });

    // Subscribe to location source changes
    this.locationSource.valueChanges.subscribe((source) => {
      if (!this.canChangeLocation) {
        return; // Don't allow changes if inventory transaction is saved
      }
      this.updateComingFromLocation(source);
    });

    this.knittingOrder.valueChanges.subscribe((knittingOrder) => {
      if (!_.isNil(knittingOrder.id)) {
        this.inventoryTransactionManager?.setGoingToCode(knittingOrder.locationCode);

        // Auto-detect location source based on knitting order's location
        if (knittingOrder.locationCode === ImportantLocations.SIMTEX_RAW_MATERIAL) {
          this.locationSource.setValue('SIMTEX', { emitEvent: false });
          this.updateComingFromLocation('SIMTEX');
        }

        if (_.isNil(this.inventoryTransaction)) {
          this.inventoryTransactionManager?.reloadInventoryTransactions(InventoryTransactionType.Transfer);
        }
      }
    });

    // Get work order based on query param id
    this.route.queryParams.subscribe((params) => {
      if (params['id']) {
        // Check for location source override from query params
        if (params['locationSource']) {
          this.locationSource.setValue(params['locationSource'], { emitEvent: false });
          this.updateComingFromLocation(params['locationSource']);
        } else if (params['bodegaVirtualInicialTejido'] === 'true') {
          this.locationSource.setValue('INITIAL', { emitEvent: false });
          this.updateComingFromLocation('INITIAL');
        } else {
          this.updateComingFromLocation(this.locationSource.value ?? 'NORMAL');
        }

        const id = params['id'];
        if (id === 'new') {
          this.breadcrumbService.setCurrent('Common_New');
          //only add transaction if it is saved requisition
          this.inventoryTransactionManagerFactory.deleteManager('requisition-details');
          return;
        }
        this.getRequisitionById(id);

        this.inventoryTransactionManager?.setInitiatorEntityKey({
          reference: params['id'],
          entityDefinitionCode: GlobalEntityDefinition.InternalRequisition, // requisition
        });
      } else if (params['addNewByKnittingOrderReference']) {
        this.breadcrumbService.setCurrent('Common_New');
        this.knittingOrder.setValue(params['addNewByKnittingOrderReference']);
        this.getLinkedKnittingOrder(params['addNewByKnittingOrderReference'], true);
      } else {
        this.router.navigate(['/yarn-requisitions']);
      }
    });
  }

  get currentFromLocationLabel(): string {
    const locationCode = this.currentFromLocationCode;
    if (!locationCode) return '';

    switch (locationCode) {
      case ImportantLocations.RAW_MATERIAL:
        return 'Normal Warehouse';
      case ImportantLocations.SIMTEX_RAW_MATERIAL:
        return 'SIMTEX Warehouse';
      case ImportantLocations.INITIAL_VIRTUAL:
        return 'Initial Virtual Warehouse';
      default:
        return locationCode;
    }
  }

  updateComingFromLocation(source: string | null): void {
    if (!this.canChangeLocation) {
      GlobalMessageUtil.error(
        'Cannot Change Location',
        'Location cannot be changed after inventory transaction has been saved. Please void this requisition and create a new one if you need to change the source location.',
      );
      // Reset to the current value
      const currentCode = this.currentFromLocationCode;
      if (currentCode === ImportantLocations.SIMTEX_RAW_MATERIAL) {
        this.locationSource.setValue('SIMTEX', { emitEvent: false });
      } else if (currentCode === ImportantLocations.INITIAL_VIRTUAL) {
        this.locationSource.setValue('INITIAL', { emitEvent: false });
      } else {
        this.locationSource.setValue('NORMAL', { emitEvent: false });
      }
      return;
    }

    switch (source) {
      case 'SIMTEX':
        this.inventoryTransactionManager?.setComingFromCode(ImportantLocations.SIMTEX_RAW_MATERIAL);
        break;
      case 'INITIAL':
        this.inventoryTransactionManager?.setComingFromCode(ImportantLocations.INITIAL_VIRTUAL);
        break;
      case 'NORMAL':
      default:
        this.inventoryTransactionManager?.setComingFromCode(ImportantLocations.RAW_MATERIAL);
        break;
    }

    // Reload inventory transactions if we have a knitting order and transaction is local (not saved)
    if (this.knittingOrder.value?.id) {
      const hasLocalTransaction = this.inventoryTransactions?.some((t) => _.isNil(t.id));
      if (hasLocalTransaction || _.isNil(this.inventoryTransaction)) {
        // Delete existing local transaction and reload with new location
        const localTransaction = this.inventoryTransactions?.find((t) => _.isNil(t.id));
        if (localTransaction) {
          this.inventoryTransactionManager?.deleteLocalInventoryTransaction(localTransaction);
        }
        this.inventoryTransactionManager?.reloadInventoryTransactions(InventoryTransactionType.Transfer);
      }
    }
  }

  updateStatus(newStatus: number) {
    if (newStatus === this.originalRequisition!.status) {
      return;
    }
    this.isLoading++;
    this.requisitionProxyService.updateStatus(this.requisition!.id!, newStatus).subscribe({
      next: () => {
        this.isLoading--;
        GlobalMessageUtil.success(
          this.translateService.instant('Common_Success'),
          this.translations['Requisition_Status_Updated'][this.translateService.currentLang as keyof Translation]
            .replace('{{newStatus}}', this.statuses[newStatus])
            .replace('{{number}}', this.requisition!.requisitionNumber!),
        );
        this.originalRequisition!.status = newStatus;
        this.status.setValue(this.originalRequisition!.status, { emitEvent: false });
        this.inventoryTransactionManager?.reloadInventoryTransactions(InventoryTransactionType.Transfer);
      },
      error: () => {
        this.isLoading--;
        this.status.setValue(this.originalRequisition!.status, { emitEvent: false });
      },
    });
  }

  get inventoryTransactions() {
    return this.inventoryTransactionManager?.inventoryTransactions();
  }

  get editRequisition() {
    return !_.isNil(this.requisition);
  }

  getRequisitionById(id: string) {
    this.isLoading++;
    this.requisitionProxyService.getById(id).subscribe({
      next: (response) => {
        if (response.data) {
          this.notes = (response.data.notes ?? []).sort((a, b) => {
            const dateA = a.dateModified ? new Date(a.dateModified).getTime() : 0;
            const dateB = b.dateModified ? new Date(b.dateModified).getTime() : 0;
            return dateB - dateA;
          });

          this.requisition = response.data;
          this.breadcrumbService.setCurrent(this.requisition.requisitionNumber);
          this.originalRequisition = _.cloneDeep(this.requisition);
          // Populate the forms with the fetched data
          this.populateData(this.requisition);
          this.isLoading--;
        }
      },
      error: () => {
        this.router.navigate(['/yarn-requisitions']);
        this.isLoading--;
      },
    });
  }

  populateData(requisition: Requisition) {
    this.requisitionsDetailsForm.patchValue({
      id: requisition.id,
      requisitionNumber: requisition.requisitionNumber,
      initiator: requisition.initiator,
      requester: requisition.requester,
      plannedIssuer: requisition.plannedIssuer,
      status: requisition.status,
    });
    this.notes = requisition.notes ?? [];
    this.status.setValue(requisition.status, { emitEvent: false });
    // getLinkedKnittingOrder
    if (requisition.initiator != null && requisition.initiator.reference?.length > 0) {
      this.getLinkedKnittingOrder(requisition.initiator.reference);
    }
    // patch requisitionEntries
  }

  getLinkedKnittingOrder(id: string, newRequisition = false) {
    this.isLoading++;
    this.knittingOrderProxyService.getById(id).subscribe((response) => {
      if (response.data) {
        this.knittingOrder.setValue(response.data);

        if (newRequisition) {
          this.requisitionsDetailsForm.patchValue({
            initiator: {
              reference: response.data.id,
              entityDefinitionCode: 6,
            },
          });
        }

        const req = { name: '', code: '', businessTypes: [0] };

        this.businessProxyService.search(req, 1, 100).subscribe({
          next: (providers) => {
            this.providersOptions = providers.data?.items ?? [];
            this.batchFabrics();
            this.isLoading--;
          },
          error: () => {
            this.isLoading--;
          },
        });
      }
    });
  }

  populateKnittingOrderRequisitionEntries(knittingOrder: KnittingOrder, fabrics: Fabric[]) {
    this.requisitionEntries.clear();
    this.selectedFabrics = [];
    this.groupedEntries = [];
    const distinctYarnCodes = fabrics
      .flatMap((fabric) => fabric.greige.yarnDetails.map((yarn) => yarn.yarnCode))
      .filter((yarnCode, index, self) => self.indexOf(yarnCode) === index);

    const command: MatchingItemMasterCommand[] = distinctYarnCodes.map((yarnCode) => ({
      yarnCode: yarnCode!,
    }));

    this.yarnProxyService.getItemMastersByYarnCode(command).subscribe({
      next: (response) => {
        if (!response.data) {
          GlobalMessageUtil.error('Error', 'No item master data found for the provided yarn codes.');
        }
        const items: Record<string, ItemMaster | null> = response.data!;
        if (items) {
          // Store yarn and item master mapping
          Object.keys(items).forEach((key) => {
            const item = items[key as keyof typeof items]!;
            this.yarnAndItemMaster[key] = item;
            this.itemMasterOptions.push(item);
          });

          knittingOrder.fabricQuantityDetails.forEach((fabric) => {
            const matchingEntries: RequisitionEntry[] =
              this.requisition?.requisitionEntries.filter((entry) => {
                // Entries saved with the fix carry the KO Entry they belong to. VariantCode alone is NOT
                // a unique key across the whole Knitting Order — it's scoped per fabric (e.g. two different
                // fabrics in the same KO can each have their own "Entry 1"), so it must be paired with
                // materialForItemCode/fabricCode. Matching on variantCode alone would leak quantity between
                // unrelated fabrics that happen to share the same Entry label.
                if (!_.isNil(entry.knittingOrderEntryVariantCode)) {
                  return entry.materialForItemCode === fabric.fabricCode && entry.knittingOrderEntryVariantCode === fabric.variantCode;
                }

                // Legacy entries (saved before this field existed) fall back to the old matching.
                const isFabricMatch = entry.materialForItemCode === fabric.fabricCode;

                const hasMatchingYarn = fabric.yarnAndProviderInfos.some(
                  (yarn) =>
                    yarn.yarnCode === entry.itemCode &&
                    yarn.provider?.id === entry.providerReference &&
                    yarn.providerLotNumber === entry.providerLotNumber,
                );

                return isFabricMatch && hasMatchingYarn;
              }) ?? [];
            const localFabric = fabrics.find((f) => f.fabricCode === fabric.fabricCode);
            const currentFabricQuantity = matchingEntries.reduce((acc, entry) => acc + entry.requiredQuantityInUnitOfMeasure, 0) || 0;
            const groupEntry: GroupedEntries = {
              itemCode: fabric.fabricCode,
              variantCode: fabric.variantCode,
              item: null,
              entries: new FormArray<FormGroup<RequisitionEntriesFormGroup>>([]),
              plannedQuantity: new FormControl(fabric.quantity),
              totalQuantity: new FormControl(currentFabricQuantity, {
                nonNullable: true,
                validators: [Validators.required, Validators.min(0.01)],
              }),
              fabric: localFabric!,
            };
            fabric.yarnAndProviderInfos.forEach((yarnWithProviderInfo) => {
              // Sum all percentages for this yarn code
              const totalUsePercentage =
                localFabric?.greige.yarnDetails
                  .filter((yarnDetail) => yarnDetail.yarnCode === yarnWithProviderInfo.yarnCode)
                  .reduce((sum, yarnDetail) => sum + (yarnDetail.usePercentage ?? 0), 0) ?? 0;

              if (totalUsePercentage === 0) {
                console.error(`Yarn details not found for yarn code: ${yarnWithProviderInfo.yarnCode}`);
                return;
              }
              const yarnQuantity = _.round((totalUsePercentage / 100) * currentFabricQuantity, 2);
              const itemMaster = this.yarnAndItemMaster[yarnWithProviderInfo.yarnCode] ?? null;
              const requisitionEntriesFormGroup = new FormGroup<RequisitionEntriesFormGroup>({
                itemCode: new FormControl(yarnWithProviderInfo.yarnCode, {
                  nonNullable: true,
                }),
                itemMaster: new FormControl<ItemMaster | null>(itemMaster ?? null),
                materialForItemCode: new FormControl(fabric.fabricCode, {
                  nonNullable: true,
                }),
                provider: new FormControl<Business | null>(yarnWithProviderInfo.provider ?? null),
                providerLotNumber: new FormControl<string | null>(yarnWithProviderInfo.providerLotNumber ?? null),
                unitOfMeasure: new FormControl(fabric.unitOfMeasure, {
                  nonNullable: true,
                }),
                requiredQuantityInUnitOfMeasure: new FormControl(yarnQuantity, {
                  nonNullable: true,
                }),
              });
              groupEntry.entries.push(requisitionEntriesFormGroup);
            });
            this.groupedEntries.push(groupEntry);
          });
        }
      },
      error: (error) => {
        console.log('🚀 ~ YarnRequisitionsDetailsComponent ~ populateKnittingOrderRequisitionEntries ~ error:', error);
      },
    });
  }

  getFabricByCode(fabricCode: string): Fabric | undefined {
    return this.allFabrics.find((fabric) => fabric.fabricCode === fabricCode);
  }

  addCommasAndDecimal(value: number): string {
    return value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  batchFabrics() {
    const knittingOrderFabricCodes = this.knittingOrder.value.fabricQuantityDetails.map((fabric) => fabric.fabricCode);

    this.fabricProxyService.batchFabrics(knittingOrderFabricCodes).subscribe({
      next: (response) => {
        if (response.data) {
          this.allFabrics = response.data;
          this.populateKnittingOrderRequisitionEntries(this.knittingOrder.value, response.data);
          // fabrics.forEach((fabric) => {
          //   this.createRequisitionEntryForms(fabric);
          // });
        }
      },
    });
    const req = { name: '', code: '', businessTypes: [0] };

    this.businessProxyService.search(req, 1, 100).subscribe({
      next: (providers) => {
        this.providersOptions = providers.data?.items ?? [];
      },
      error: (error) => {
        console.error('Error fetching providers:', error);
      },
    });
  }

  isGroupEntryValid(group: GroupedEntries): boolean {
    //check that all entries have provider and providerLotNumber
    return group.entries.controls.every((entry) => {
      const provider = entry.get('provider')?.value;
      const providerLotNumber = entry.get('providerLotNumber')?.value;
      return provider && providerLotNumber;
    });
  }

  setRequisitionsDetailsForm() {
    this.requisitionsDetailsForm = this.fb.group({
      id: new FormControl(''),
      requisitionNumber: new FormControl(''),
      initiator: new FormControl<EntityKeyByReference | null>(null, [Validators.required]),
      //work station
      requester: new FormControl<EntityKeyByReference | null>({ reference: '67ffccbff36f9bcb55a25cd5', entityDefinitionCode: 8 }, [
        Validators.required,
      ]),
      // warehouse
      plannedIssuer: new FormControl<EntityKeyByReference | null>({ reference: '6798fc5ea05528e84cacb52e', entityDefinitionCode: 0 }, [
        Validators.required,
      ]),
      status: new FormControl(0),
    });
  }

  public fetchKnittingOrders = (query: string): Observable<KnittingOrder[]> => {
    const command: KnittingOrderSearchCommand = {
      knittingOrderNumber: query,
      statuses: [KnittingOrderStatus.Approved, KnittingOrderStatus.InProgress],
    };

    return this.knittingOrderProxyService.search(command).pipe(map((response) => response.data?.items ?? []));
  };

  providerOptionsTypeAhead(event: AutoCompleteCompleteEvent): void {
    const term = event.query;
    const command: BusinessSearch = {
      name: term,
      businessTypes: [BusinessEntityType.Provider],
    };

    this.businessProxyService.search(command, 1, 100).subscribe({
      next: (providers) => {
        this.providersOptions = providers.data?.items ?? [];
      },
      error: (error) => {
        console.error('Error fetching providers:', error);
      },
    });
  }

  onProviderSelect(event: AutoCompleteSelectEvent, entryFormGroup: FormGroup<RequisitionEntriesFormGroup>) {
    //const provider = event.value;
    const groupIndex = this.requisitionEntries.controls.findIndex((group) => group === entryFormGroup);
    const itemGroup = this.requisitionEntries.at(groupIndex) as FormGroup<RequisitionEntriesFormGroup>;

    if (itemGroup) {
      // itemGroup.patchValue({
      //   providerReference: provider.id,
      // });

      this.requisitionEntries.value[groupIndex] = itemGroup.value;
    }
  }

  onProviderLotIDSelect(event: SelectChangeEvent, entryFormGroup: FormGroup<RequisitionEntriesFormGroup>) {
    const groupIndex = this.requisitionEntries.controls.findIndex((group) => group === entryFormGroup);
    const itemGroup = this.requisitionEntries.at(groupIndex) as FormGroup<RequisitionEntriesFormGroup>;

    if (itemGroup) {
      itemGroup.patchValue({
        providerLotNumber: event.value,
      });

      this.requisitionEntries.value[groupIndex] = itemGroup.value;
    }
  }

  onSelectKnittingOrderToUse(event: AutoCompleteSelectEvent) {
    this.groupedEntries = [];
    const selectedKnittingOrder: KnittingOrder = event.value;
    this.requisitionsDetailsForm.patchValue({
      initiator: {
        reference: selectedKnittingOrder.id,
        entityDefinitionCode: 6,
      },
    });
    this.requisitionsDetailsForm.patchValue({
      entityKey: {
        reference: selectedKnittingOrder.id,
        referenceType: 0,
      },
    });
    this.batchFabrics();
  }

  fabricTypeAhead(event: AutoCompleteCompleteEvent): void {
    const term = (event.query ?? '').toLowerCase();

    const allFabrics = this.knittingOrder.value.fabricQuantityDetails;

    if (term === '') {
      this.fabricSuggestions = allFabrics.map((item) => item.fabricCode);
    }

    this.fabricSuggestions = allFabrics.filter((item) => item.fabricCode.toLowerCase().includes(term)).map((item) => item.fabricCode);
  }

  calcRequiredItems(value: number, index: number) {
    const numberValue = Number(value);
    const group = this.groupedEntries[index];

    group.entries.controls.forEach((entry) => {
      const yarnCode = entry.get('itemCode')?.value;

      // Sum all percentages for this yarn code
      const totalUsePercentage = group.fabric.greige.yarnDetails
        .filter((yarn) => yarn.yarnCode === yarnCode)
        .reduce((sum, yarn) => sum + (yarn.usePercentage ?? 0), 0);

      const newQty = (totalUsePercentage / 100) * numberValue;

      entry.get('requiredQuantityInUnitOfMeasure')?.setValue(newQty);
    });
  }

  getYarnUsePercentage(group: GroupedEntries, yarnCode: string): number {
    // Sum all percentages for this yarn code in the fabric
    return group.fabric.greige.yarnDetails
      .filter((yarn) => yarn.yarnCode === yarnCode)
      .reduce((sum, yarn) => sum + (yarn.usePercentage ?? 0), 0);
  }

  getGroupVariantCode(group: GroupedEntries): string {
    return group.variantCode ?? '';
  }

  calcRequiredItemsTotal(): RequiredItem[] {
    // eslint-disable-next-line @typescript-eslint/consistent-indexed-object-style
    const yarnQuantities: {
      [yarnCode: string]: number;
    } = {};
    const requiredItem: RequiredItem[] = [];

    this.groupedEntries.forEach((group) => {
      group.entries.controls.forEach((entry) => {
        const yarnCode = this.yarnAndItemMaster[entry.get('itemCode')!.value]?.itemCode;
        const quantity = entry.get('requiredQuantityInUnitOfMeasure')?.value;
        if (yarnCode && quantity) {
          if (yarnQuantities[yarnCode]) {
            yarnQuantities[yarnCode] += quantity;
          } else {
            yarnQuantities[yarnCode] = quantity;
          }
        }
      });
    });

    for (const yarnCode in yarnQuantities) {
      requiredItem.push({
        itemCode: yarnCode,
        itemMaster: this.itemMasterOptions.find((item) => item.itemCode === yarnCode) || null,
        totalQuantity: yarnQuantities[yarnCode],
        loadedQuantity:
          this.inventoryTransactionNeededElements().find((element) => element.Item.itemCode === yarnCode)?.LoadedQuantity ?? 0,
      });
    }

    return requiredItem;
  }

  clearAutocomplete(autoComplete: AutoComplete) {
    autoComplete.inputEL!.nativeElement.value = ''; // clear p-autocomplete visual
  }

  setRequisitionEntriesAsTouched() {
    this.requisitionEntries.controls.forEach((control: FormGroup) => {
      Object.keys(control.controls).forEach((key) => {
        control.get(key)?.markAsTouched();
      });
    });
  }

  inventoryTransactionNeededElements(): InventoryTransactionNeededElement[] {
    let elements: InventoryTransactionNeededElement[] = [];

    this.groupedEntries.forEach((group) => {
      if (this.isGroupEntryValid(group)) {
        group.entries.controls.forEach((entry) => {
          elements.push({
            Provider: entry.controls.provider.value!,
            LotNumber: entry.controls.providerLotNumber.value!,
            Item: entry.controls.itemMaster.value!,
            NeededQuantity: entry.controls.requiredQuantityInUnitOfMeasure.value,
            LoadedQuantity: entry.controls.loadedQuantityInUnitOfMeasure?.value ?? 0,
          });
        });
      }
    });

    elements = elements.filter((element) => element.NeededQuantity > 0);
    return this.groupTransactionNeededQuantityElements(elements);
  }

  updateInventoryTransactionNeededElement(element: InventoryTransactionNeededElement) {
    this.groupedEntries.forEach((group) => {
      if (this.isGroupEntryValid(group)) {
        group.entries.controls.forEach((entry) => {
          if (
            entry.controls.itemMaster.value?.itemCode === element.Item.itemCode &&
            entry.controls.provider.value?.id === element.Provider.id &&
            entry.controls.providerLotNumber.value === element.LotNumber
          ) {
            if (!_.isNil(element.LoadedQuantity)) {
              entry.controls.loadedQuantityInUnitOfMeasure = new FormControl(element.LoadedQuantity, {
                nonNullable: true,
              });
            }
          }
        });
      }
    });
  }

  groupTransactionNeededQuantityElements(transactions: InventoryTransactionNeededElement[]): InventoryTransactionNeededElement[] {
    // eslint-disable-next-line @typescript-eslint/consistent-indexed-object-style
    const elements: { [key: string]: InventoryTransactionNeededElement } = {};

    for (const transaction of transactions) {
      const clave = `${transaction.Item.itemCode}-${transaction.LotNumber}-${transaction.Provider.code}`;

      if (elements[clave]) {
        elements[clave].NeededQuantity += transaction.NeededQuantity;
      } else {
        elements[clave] = { ...transaction };
      }
    }

    return Object.values(elements);
  }

  downloadRequisition() {
    if (_.isNil(this.inventoryTransactions) || _.isEmpty(this.inventoryTransactions)) {
      GlobalMessageUtil.error('Error', this.translateService.instant('Yarn_Requisitions_Details_Fail_To_Download_Report'), 3000);
      return;
    }

    const inventoryTransaction = this.inventoryTransactions[0];
    const lotItemKeys = InventoryTransactionService.GetLotItemKeys(inventoryTransaction);

    if (_.isEmpty(lotItemKeys)) {
      GlobalMessageUtil.error('Error', this.translateService.instant('Yarn_Requisitions_Details_Fail_To_Download_Report'), 3000);
      return;
    }

    const stock_from = this.inventoryTransactionManager?.getLocation(inventoryTransaction?.fromLocationCode ?? '');
    const stock_to = this.inventoryTransactionManager?.getLocation(inventoryTransaction?.toLocationCode ?? '');
    // First get lot items
    this.lotItemProxyService.getByLotNumbers(lotItemKeys.map((k) => k.number)).subscribe({
      next: (response) => {
        const items = response.data ?? [];
        const reportData: RequisitionReport = {
          transfer: {
            company_name: SiteService.getSelectedSite()?.name ?? 'Northern Textiles',
            title: 'Yarn Requisition',
            requisition_number: this.requisition?.requisitionNumber || '',
            transfer_type: this.transactionType[inventoryTransaction?.transactionType ?? 0] ?? '',
            stock_from: stock_from?.name.en ?? '',
            date: inventoryTransaction?.modifiedDate
              ? format(new Date(inventoryTransaction.modifiedDate), 'dd/MM/yyyy')
              : new Date().toLocaleDateString(),
            stock_to: stock_to?.name.en ?? '',
            work_order_no: this.knittingOrder.value?.workOrderNumber ?? '',
            knitting_no: this.knittingOrder.value?.knittingOrderNumber ?? '',
            yarn_lot:
              Array.from(
                new Set(
                  this.knittingOrder.value?.fabricQuantityDetails
                    ?.flatMap((detail) => (detail.yarnAndProviderInfos ?? []).map((yarnInfo) => yarnInfo.providerLotNumber))
                    .filter(Boolean),
                ),
              ).join(', ') ?? '',
            remarks: '',
          },
          items:
            lotItemKeys.map((key, index) => {
              const lotItem = items.find((item) => item.number === key.number);

              return {
                sno: index + 1,
                item_code: lotItem?.item?.itemCode ?? '',
                description: this.localHelperService.getLocalizedValue(lotItem?.item?.itemDescription) ?? '',
                uom: this.unitOfMeasures[lotItem?.item?.unitOfMeasure ?? 0] ?? '',
                provider_name: this.groupedEntries[0].entries.value[0].provider?.name ?? '',
                lot_item_number: key.number ?? '',
                provider_label_id: lotItem?.providerLabelNumber ?? '',
                net_quantity: _.round(lotItem?.netQuantity ?? 0, 2),
                gross_quantity: _.round(lotItem?.grossQuantity ?? 0, 2),
              };
            }) ?? [],
          total_net_quantity: _.round(_.sumBy(items, (item) => item.netQuantity) ?? 0, 2),
          total_gross_quantity: _.round(_.sumBy(items, (item) => item.grossQuantity) ?? 0, 2),
          signs: [{ label: 'Prepared By' }, { label: 'Checked By' }, { label: 'Approved By' }],
          footer: {
            username: this.requisition?.createdBy || 'Unknown User',
            date: format(new Date(), 'MM/dd/yyyy'),
          },
        };

        this.getRequisitionReport(reportData);
      },
    });
  }

  getRequisitionReport(report: RequisitionReport) {
    this.reportProxyService.getRequisitionReport(report).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const printWindow = window.open(url, '_blank');
        if (printWindow) {
          printWindow.document.write(`
            <html>
              <body style="margin:0">
                <iframe
                  src="${url}"
                  type="application/pdf"
                  style="width:100vw;height:100vh;border:none"
                ></iframe>
              </body>
            </html>
          `);
          printWindow.document.close();
        }
      },
    });
  }

  getEntityKeyByReferenceFieldToDisplay(EntityKey: EntityKeyByReference | undefined): string {
    if (!EntityKey) {
      return '';
    }
    switch (EntityKey.entityDefinitionCode) {
      case 0:
        return this.warehouses.find((warehouse) => warehouse.id === EntityKey.reference)?.fieldToDisplay ?? '';
      case 8:
        return this.workStations.find((workStation) => workStation.id === EntityKey.reference)?.fieldToDisplay ?? '';
      case 5:
        return this.providers.find((provider) => provider.id === EntityKey.reference)?.fieldToDisplay ?? '';
      default:
        return 'Unknown';
    }
  }

  save() {
    this.setRequisitionEntriesAsTouched();
    this.requisitionsDetailsFormSubmitted = true;

    // Only groups (Knitting Order Entries) the user actually requested quantity for should be
    // saved — a group with a valid provider/lot but totalQuantity 0 was never requested, and must
    // not turn into a phantom zero-quantity line tied to a KO Entry the user never touched.
    const requestedEntries: RequisitionEntry[] = this.groupedEntries
      .filter((g) => this.isGroupEntryValid(g) && (g.totalQuantity.value ?? 0) > 0)
      .flatMap((group) =>
        group.entries.controls
          .map((entry) => entry.getRawValue())
          .filter((entryValue) => entryValue.requiredQuantityInUnitOfMeasure > 0)
          .map((entryValue) => {
            const requisitionEntry: RequisitionEntry = {
              itemCode: entryValue.itemCode,
              materialForItemCode: group.itemCode!,
              providerReference: entryValue.provider!.id!,
              providerLotNumber: entryValue.providerLotNumber!,
              unitOfMeasure: entryValue.unitOfMeasure,
              requiredQuantityInUnitOfMeasure: entryValue.requiredQuantityInUnitOfMeasure,
              knittingOrderEntryVariantCode: group.variantCode,
            };
            return requisitionEntry;
          }),
      );

    if (
      this.requisitionsDetailsForm.valid &&
      this.groupedEntries.filter((g) => this.isGroupEntryValid(g)).every((group) => group.entries.valid) &&
      requestedEntries.length > 0
    ) {
      const dataForSend: Requisition = {
        ...this.requisitionsDetailsForm.getRawValue(),
        requisitionEntries: requestedEntries,
        notes: this.notes,
      };

      if (dataForSend?.id) {
        this.isLoading++;

        this.requisitionProxyService.update(dataForSend.id, dataForSend).subscribe({
          next: (response) => {
            if (response.success) {
              GlobalMessageUtil.success(
                this.translations['Success'][this.translateService.currentLang as keyof Translation],
                this.translations['Requisition_Updated_Successfully'][this.translateService.currentLang as keyof Translation].replace(
                  '{{number}}',
                  this.requisition?.requisitionNumber ?? '',
                ),
                2000,
              );
              this.isLoading--;
              setTimeout(() => {
                this.router.navigate(['/yarn-requisitions']);
              }, 2000);
            }
          },
          error: () => {
            this.isLoading--;
          },
        });
      } else {
        this.isLoading++;

        this.requisitionProxyService.create(dataForSend).subscribe({
          next: (response) => {
            this.isLoading--;
            if (response.data) {
              GlobalMessageUtil.success(
                this.translations['Success'][this.translateService.currentLang as keyof Translation],
                this.translations['Requisitions_Created_Successfully'][this.translateService.currentLang as keyof Translation].replace(
                  '{{name}}',
                  response.data?.requisitionNumber ?? '',
                ),
                3000,
              );
              setTimeout(() => {
                this.router.navigate(['/yarn-requisitions'], {
                  queryParams: {
                    id: response.data?.id,
                  },
                  replaceUrl: true,
                });
              }, 2000);
            }
          },
          error: (error) => {
            this.isLoading--;
            console.error('Error creating requisition:', error);
          },
        });
      }

      this.requisitionsDetailsFormSubmitted = false;
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: this.translations['Form_Has_Invalid_Fields'][this.translateService.currentLang as keyof Translation],
        life: 3000,
      });
    }
  }

  cancel() {
    this.router.navigate(['/yarn-requisitions']);
  }

  // #region QR Scanner methods
  onQrCodeScanned(code: string): void {
    console.log('QR Code scanned:', code);
    if (!this.uniqueScanned.includes(code)) {
      this.uniqueScanned.push(code);
    }
    //this.lotItemSearch.setValue(code);
  }
  // #endregion
  onViewMore(): void {
    this.openNotesModal();
  }

  openNotesModal(): void {
    const modalData = {
      notes: this.notes,
      userLite: this.userLite,
      viewOnly: false,
    };

    this.modalService.open(NotesSidebarComponent, {}, modalData).subscribe({
      next: (result) => {
        if (result && result.action === 'noteAdded') {
          this.notes = result.data.notes;
        }
      },
      error: (error) => {
        console.error('Error handling notes modal result:', error);
      },
    });
    setTimeout(() => {
      const modalElement = document.querySelector('.panel.modal') as HTMLElement;
      if (modalElement) modalElement.style.maxWidth = '50%';
      modalElement.style.width = '50%';
    }, 100);
  }

  onAddNote() {
    const idRequisition = this.requisitionsDetailsForm.getRawValue().id;

    this.requisitionProxyService.addNotes(idRequisition, this.notes[0]).subscribe({
      next: (response) => {
        if (response.success) {
          GlobalMessageUtil.success(
            this.translations['Success'][this.translateService.currentLang as keyof Translation],
            this.translations['Note_Added_Successfully'][this.translateService.currentLang as keyof Translation].replace(
              '{{number}}',
              this.requisition?.requisitionNumber ?? '',
            ),
            2000,
          );
          this.isLoading--;
        }
      },
      error: () => {
        this.isLoading--;
      },
    });
  }
}
